import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { DataGrid } from '@mui/x-data-grid';
import { frFR } from '@mui/x-data-grid/locales';
import Bouton from '../Bouton/Bouton';
import styles from './Tableau.module.scss';
import '../../../ressources/StyleGlobal/personnaliser.css';
import * as s from '../../../ressources/StyleGlobal/StyleGlobal';
import AccesRestreins from '../../../hoc/AccesRestreins/AccesRestreins';
import * as type_btn from '../../../ressources/glossaires/glossaire_type_de_bouton';
import { checkMot } from '../../../ressources/functionJS/checkMot';
import { commonDataGridStyles } from '../../../ressources/functionJS/dataGridStyles';
import { VscSearch } from 'react-icons/vsc';
import { AiOutlineClose } from 'react-icons/ai';
import { estDansArborescenceProjets } from 'services/reducteurs/interfaceClient';
const MySearch = (props) => {
  const [searchValue, setSearchValue] = useState('');

  let input;
  const handleClick = (e) => {
    input.value = '';
    props.onSearch(input.value);
    setSearchValue(e.target.value);
  };
  const handleChange = (e) => {
    setSearchValue(e.target.value);
    props.onSearch(e.target.value);
  };

  return (
    <div className={[s.row, styles.box_search].join(' ')}>
      <Bouton
        ajoutClass={[styles.bts_search].join(' ')}
        cliquer={handleClick}
        type={type_btn.outil}
        rond={true}
      >
        {searchValue ? <AiOutlineClose size={15} /> : <VscSearch size={15} />}
      </Bouton>
      <input
        ref={(n) => (input = n)}
        className={[s.col, styles.serch, s.form_control].join(' ')}
        type="text"
        placeholder={checkMot('recherche')}
        onChange={handleChange}
      />
    </div>
  );
};

const normalizeValue = (value) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.join(' ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const Tableau = (props) => {
  const [searchValue, setSearchValue] = useState('');
  const [selectionModel, setSelectionModel] = useState(
    props.selectionnerLignes?.selected || []
  );
  const prevSelectionRef = useRef(selectionModel);

  useEffect(() => {
    if (Array.isArray(props.selectionnerLignes?.selected)) {
      setSelectionModel(props.selectionnerLignes.selected);
    }
  }, [props.selectionnerLignes?.selected]);

  useEffect(() => {
    prevSelectionRef.current = selectionModel;
  }, [selectionModel]);

  const columns = useMemo(() => {
    return (props.colonnes || [])
      .map((col) => {
        if (!col || !col.dataField) return null;
        const headerContent = col.text ?? '';
        const hasHeaderStyle = !!col.headerStyle;
        const styleWidth = col.style?.width;
        const numericWidth =
          typeof styleWidth === 'number'
            ? styleWidth
            : typeof styleWidth === 'string'
              ? parseInt(styleWidth, 10)
              : undefined;
        const width = col.width || numericWidth;
        const minWidth = col.minWidth || numericWidth || 120;
        const base = {
          field: col.dataField,
          headerName:
            typeof headerContent === 'string' ? headerContent : undefined,
          flex: col.flex !== undefined ? col.flex : width ? undefined : 1,
          width: width || undefined,
          minWidth,
          sortable: !!col.sort,
          hide: !!col.hidden,
          editable: !!col.editable,
          align: col.align || 'left',
          headerAlign: col.headerAlign || 'left',
        };

        const applyFormatter = (params) => {
          const rawValue = params.value;
          if (typeof col.formatter === 'function') {
            return col.formatter(rawValue, params.row);
          }
          return rawValue ?? '';
        };

        const renderCell = (params) => {
          const value = applyFormatter(params);
          if (col.style) {
            return <div style={col.style}>{value}</div>;
          }
          return value;
        };

        const renderHeader =
          typeof headerContent === 'string' && !hasHeaderStyle
            ? undefined
            : () => (
                <div style={col.headerStyle || undefined}>{headerContent}</div>
              );

        return {
          ...base,
          renderCell,
          renderHeader,
        };
      })
      .filter(Boolean);
  }, [props.colonnes]);

  const rowIdGetter = useCallback(
    (row) => row?.[props.cleLigne] ?? row?.id,
    [props.cleLigne]
  );

  const rows = useMemo(
    () => (Array.isArray(props.donnees) ? props.donnees : []),
    [props.donnees]
  );

  const hiddenIds = useMemo(
    () => new Set(props.lignesCache || []),
    [props.lignesCache]
  );

  const visibleRows = useMemo(
    () =>
      rows.filter((row) => {
        const rowId = rowIdGetter(row);
        return rowId !== null && rowId !== undefined && !hiddenIds.has(rowId);
      }),
    [rows, hiddenIds, rowIdGetter]
  );

  const searchFields = useMemo(
    () => (props.colonnes || []).map((col) => col.dataField).filter(Boolean),
    [props.colonnes]
  );

  const filteredRows = useMemo(() => {
    if (!searchValue) return visibleRows;
    const needle = searchValue.toLowerCase();
    return visibleRows.filter((row) =>
      searchFields.some((field) =>
        normalizeValue(row[field]).toLowerCase().includes(needle)
      )
    );
  }, [visibleRows, searchFields, searchValue]);

  const rowMap = useMemo(
    () => new Map(filteredRows.map((row) => [rowIdGetter(row), row])),
    [filteredRows, rowIdGetter]
  );

  const handleSelectionModelChange = useCallback(
    (newSelection) => {
      const prev = prevSelectionRef.current || [];
      const next = Array.isArray(newSelection) ? newSelection : [];

      if (props.selectionnerLignes?.onSelect) {
        const added = next.filter((id) => !prev.includes(id));

        added.forEach((id) => {
          const row = rowMap.get(id);
          if (row) props.selectionnerLignes.onSelect(row, true);
        });
      }

      if (props.selectionnerLignes?.onSelectAll) {
        if (next.length === filteredRows.length && filteredRows.length > 0) {
          props.selectionnerLignes.onSelectAll(true, filteredRows);
        } else if (next.length === 0 && prev.length > 0) {
          props.selectionnerLignes.onSelectAll(false, filteredRows);
        }
      }

      setSelectionModel(next);
    },
    [filteredRows, props.selectionnerLignes, rowMap]
  );

  const pageSizeOptions = useMemo(() => {
    const base = [50, 100];
    if (rows.length > 0) base.push(rows.length);
    return Array.from(new Set(base)).filter((v) => Number.isFinite(v) && v > 0);
  }, [rows.length]);

  const resolvedPageSizeOptions = useMemo(
    () => props.pageSizeOptions || pageSizeOptions,
    [props.pageSizeOptions, pageSizeOptions]
  );

  const initialPageSize =
    props.initialPageSize || resolvedPageSizeOptions[0] || 50;

  const sortModel = useMemo(() => {
    if (!Array.isArray(props.defaultSorted)) return [];
    return props.defaultSorted
      .map((sortItem) => ({
        field: sortItem.dataField,
        sort: sortItem.order,
      }))
      .filter((item) => item.field && item.sort);
  }, [props.defaultSorted]);

  const initialState = useMemo(() => {
    const visibilityModel = {};
    (props.colonnes || []).forEach((col) => {
      if (col.hidden) {
        visibilityModel[col.dataField] = false;
      }
    });
    return {
      pagination: {
        paginationModel: { pageSize: initialPageSize, page: 0 },
      },
      sorting: { sortModel },
      columns: {
        columnVisibilityModel: visibilityModel,
      },
    };
  }, [initialPageSize, sortModel, props.colonnes]);

  const selectionMode = props.selectionnerLignes?.mode || 'checkbox';
  const checkboxSelection = selectionMode === 'checkbox';
  const disableMultipleRowSelection = selectionMode === 'radio';
  const disableRowSelectionOnClick =
    props.selectionnerLignes?.clickToSelect === false;
  const PROJET_TELT = 1084;
  const arborescence_projets = useSelector(
    (state) => state.interfaceClient.arborescence_projets
  );
  const project_id_url = useSelector(
    (state) => state.interfaceClient.project_id_url
  );
  const tableClassName = props.ajoutClass
    ? `${styles.TableauBootstrap} ${props.ajoutClass}`
    : styles.TableauBootstrap;
  return (
    <Fragment>
      <div className={[styles.Tableau].join(' ')}>
        <div className={[styles.BarreOutil].join(' ')}>
          {props.hide_general_search === true ? null : (
            <MySearch onSearch={setSearchValue} />
          )}
          {estDansArborescenceProjets(
            arborescence_projets,
            PROJET_TELT,
            project_id_url
          ) && (
            <AccesRestreins
              {...(Array.isArray(props.privilegeCreer)
                ? { listNumPrivilege: props.privilegeCreer }
                : { numPrivilege: props.privilegeCreer })}
            >
              <Bouton
                hoverText={checkMot('creer')}
                type={type_btn.outil}
                rond={true}
                cliquer={props.creer}
              >
                {props.iconeCreer}
              </Bouton>
            </AccesRestreins>
          )}
          {props.children}
        </div>
        <div className={tableClassName}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            getRowId={rowIdGetter}
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={handleSelectionModelChange}
            checkboxSelection={checkboxSelection}
            disableMultipleRowSelection={disableMultipleRowSelection}
            disableRowSelectionOnClick={disableRowSelectionOnClick}
            pageSizeOptions={resolvedPageSizeOptions}
            initialState={initialState}
            getRowHeight={() => 'auto'}
            sx={commonDataGridStyles}
            localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
            {...props.dataGridProps}
          />
        </div>
      </div>
    </Fragment>
  );
};

export default Tableau;
